/**
 * On-screen virtual D-pad overlays for the Frogger game.
 *
 * Renders the `frogger.controls` sprite in the bottom-left AND bottom-right
 * corners and divides each into four invisible hit zones (up / down / left /
 * right) so users can play with one hand on phones or alongside swipes on
 * desktop.
 *
 * Each tap fires the same `onMove(direction)` callback the swipe handler
 * uses, so gameplay logic stays in one place.
 */

import { Container, Graphics } from '../../../vendor/pixi.min.mjs';

const PAD_SIZE = 200;
const PAD_MARGIN = 24;
const PAD_ALPHA = 0.85;

export class FroggerControls {
    container = new Container();
    _game = null;
    _onMove = null;
    /** @type {Container[]} */
    _pads = [];

    /**
     * @param {*} game
     * @param {(direction: 'up'|'down'|'left'|'right') => void} onMove
     */
    constructor(game, onMove) {
        this._game = game;
        this._onMove = onMove;
        this._build();
    }

    _build() {
        const skin = this._game.theme;
        const baseY = this._game.height - PAD_SIZE - PAD_MARGIN;

        const left = this._buildPad();
        left.x = PAD_MARGIN;
        left.y = baseY;
        this.container.addChild(left);
        this._pads.push(left);

        const right = this._buildPad();
        right.x = this._game.width - PAD_SIZE - PAD_MARGIN;
        right.y = baseY;
        this.container.addChild(right);
        this._pads.push(right);
    }

    _buildPad() {
        const pad = new Container();

        const sprite = this._game.theme?.createSprite(
            'frogger.controls', PAD_SIZE, PAD_SIZE,
            { anchorX: 0, anchorY: 0, fitMode: 'contain' },
        );
        if (sprite) {
            sprite.alpha = PAD_ALPHA;
            pad.addChild(sprite);
        } else {
            const fallback = new Graphics();
            fallback.circle(PAD_SIZE / 2, PAD_SIZE / 2, PAD_SIZE / 2 - 4);
            fallback.fill({ color: 0x0f172a, alpha: 0.55 });
            fallback.stroke({ color: 0xffffff, alpha: 0.6, width: 2 });
            pad.addChild(fallback);
        }

        const z = PAD_SIZE / 3;
        const zones = [
            { dir: 'up',    x: z,     y: 0,     w: z, h: z },
            { dir: 'down',  x: z,     y: 2 * z, w: z, h: z },
            { dir: 'left',  x: 0,     y: z,     w: z, h: z },
            { dir: 'right', x: 2 * z, y: z,     w: z, h: z },
        ];
        for (const zo of zones) {
            const hit = new Graphics();
            hit.rect(zo.x, zo.y, zo.w, zo.h);
            hit.fill({ color: 0xffffff, alpha: 0.001 });
            hit.eventMode = 'static';
            hit.cursor = 'pointer';
            hit.on('pointertap', () => {
                if (this._onMove) this._onMove(zo.dir);
            });
            pad.addChild(hit);
        }
        return pad;
    }

    /** Toggle visibility (used on game-over / win). */
    setVisible(v) {
        this.container.visible = !!v;
    }

    destroy() {
        for (const pad of this._pads) {
            pad.removeChildren();
            if (pad.parent) pad.parent.removeChild(pad);
        }
        this._pads = [];
        if (this.container.parent) this.container.parent.removeChild(this.container);
        this.container.removeChildren();
        this._onMove = null;
        this._game = null;
    }
}
